import React from "react";
import { useScreenDimensions, useDevice } from "../_layout";
import { useState } from "react";
import {
  StyleSheet,
  Text,
  View,
  TouchableOpacity,
  ScrollView,
  Linking,
} from "react-native";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";

import { useTranslation } from "react-i18next";
import { Ionicons as IoniconsLib, AntDesign } from "@expo/vector-icons";
import GettingStartedView from "./GettingStartedView";
import { observer } from "mobx-react";

const TutorialPanel = observer(({ coach }) => {
  const { screenSize } = useScreenDimensions();
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  const [isHovered, setIsHovered] = useState(false);
  // compute completed steps count (step 1 is always completed)
  const stepCompletedBooleans = [
    true, // Step 1 always completed
    !coach.onboardingShowBlock, // Step 2
    !coach.onboardingShowAthleteApp, // Step 3
    !coach.onboardingShowInviteClient, // Step 4
  ];
  const showingPanel = coach.showingTutorialPanel;
  // Fill left-to-right based on how many steps are completed
  const completedSteps = stepCompletedBooleans.filter(Boolean).length;

  const ClosePanelButton = () => {
    const [isCloseHovered, setIsCloseHovered] = useState(false);

    return (
      <TouchableOpacity
        style={{ alignSelf: "flex-start", justifyContent: "flex-start" }}
        onPress={() => {
          coach.setProperty("showingTutorialPanel", false);
          setIsHovered(false);
          setIsCloseHovered(false);
        }}
        onMouseEnter={() => !isMobile && setIsCloseHovered(true)}
        onMouseLeave={() => setIsCloseHovered(false)}
      >
        <IoniconsLib
          name="close-outline"
          size={24}
          color={"lightgray"}
          style={{ opacity: isCloseHovered ? 0.5 : 1 }}
        />
      </TouchableOpacity>
    );
  };

  const BookCallButton = () => {
    const [hovered, setHovered] = useState(false);

    return (
      <TouchableOpacity
        style={[
          styles.bookCallButton,
          {
            backgroundColor: hovered
              ? "rgba(248, 250, 250, 1)"
              : Colors.whiteColor,
          },
        ]}
        onPress={() =>
          Linking.openURL("https://calendly.com/efortapp/30min").catch(
            console.error
          )
        }
        onMouseEnter={() => !isMobile && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <View style={{ flexDirection: "row" }}>
          <AntDesign
            name="calendar"
            size={hovered ? 25 : 24}
            color={Colors.bubbleColor}
          />
          <Text
            style={[
              styles.bookCallText,
              { fontWeight: hovered ? "700" : "600" },
            ]}
          >
            {t("bookCall-string")}
          </Text>
        </View>
      </TouchableOpacity>
    );
  };

  if (completedSteps >= 4) return null;

  return (
    <>
      {showingPanel ? (
        <View
          style={[
            styles.panelContainer,
            {
              right: screenSize === "small" ? 8 : 24,
              shadowColor: "#000",
              shadowOpacity: 0.1,
              shadowRadius: 6,
              shadowOffset: { width: 0, height: 4 },
              backgroundColor: Colors.whiteColor,
            },
          ]}
        >
          <View
            style={{
              backgroundColor: Colors.bubbleColor,
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              paddingBottom: 16,
            }}
          >
            <View style={[GlobalStyles.HStack, styles.panelHeaderContent]}>
              <Text
                style={{
                  fontSize: 18,
                  color: Colors.whiteColor,
                  fontWeight: "500",
                }}
              >
                {t("gettingStarted-string")}
              </Text>
              <ClosePanelButton />
            </View>

            <Text
              style={[
                styles.panelSubtitle,
                { color: "lightgray", marginBottom: 8 },
              ]}
            >
              {t("learnEfortTutorial-string", { coachName: coach.name })}
            </Text>
            <View style={styles.percentageBarContainer}>
              <Text style={styles.percentageText}>{completedSteps * 25}%</Text>
            </View>
            <View
              style={{
                backgroundColor: Colors.whiteColor,
                padding: 8,
                marginHorizontal: 16,
                borderRadius: 12,
                justifyContent: "center",
              }}
            >
              <View style={styles.progressBarBackground}>
                <View
                  style={[
                    styles.progressBarFilled,
                    { width: `${completedSteps * 25}%` },
                  ]}
                />
              </View>
            </View>
          </View>

          <ScrollView
            contentContainerStyle={{ flex: 1 }}
            style={{ margin: 16 }}
            showsVerticalScrollIndicator={false}
          >
            <GettingStartedView coach={coach} />
          </ScrollView>
          {/* <BookCallButton /> */}
        </View>
      ) : (
        <TouchableOpacity
          style={[
            GlobalStyles.HStack,
            styles.floatingButton,
            {
              opacity: isHovered ? 0.7 : 1,
              right: screenSize === "small" ? 8 : 24,
              bottom: screenSize === "small" ? 60 : 64,
            },
          ]}
          onPress={() => coach.setProperty("showingTutorialPanel", true)}
          onMouseEnter={() => !isMobile && setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <Text style={styles.floatingButtonText}>
            {t("gettingStarted-string")}
          </Text>
          {completedSteps < 4 && (
            <View style={styles.notificationBadge}>
              <Text style={styles.notificationText}>{4 - completedSteps}</Text>
            </View>
          )}
        </TouchableOpacity>
      )}
    </>
  );
});

const styles = StyleSheet.create({
  floatingButton: {
    height: 48,
    borderRadius: 16,
    backgroundColor: Colors.bubbleColor,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    paddingHorizontal: 10,
    alignItems: "center",
    position: "absolute",
  },
  floatingButtonText: {
    color: Colors.whiteColor,
    fontSize: 16,
    fontWeight: "600",
  },
  notificationBadge: {
    position: "absolute",
    top: -6,
    right: -6,
    backgroundColor: Colors.whiteColor,
    borderRadius: 12,
    width: 22,
    height: 22,
    minWidth: 16,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOpacity: 0.15,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: -2 },
  },
  notificationText: {
    color: Colors.blackColor,
    fontSize: 12,
    fontWeight: "600",
  },
  panelContainer: {
    borderRadius: 16,
    backgroundColor: "rgba(234, 234, 234, 1)",
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    position: "absolute",
    bottom: 64,
    maxHeight: "90%",
    width: 400,
    maxWidth: "94%",
  },
  panelHeader: {
    position: "absolute",
    borderRadius: 16,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: Colors.bubbleColor,
    flex: 1,
    top: 0,
    left: 0,
    right: 0,
    height: 90,
    zIndex: -2,
  },
  panelHeaderContent: {
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
    margin: 16,
  },
  panelSubtitle: {
    color: Colors.grayColor,
    marginHorizontal: 16,
  },
  percentageBarContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 4,
    marginHorizontal: 16,
  },
  percentageText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.whiteColor,
  },
  progressBarBackground: {
    height: 8,
    // backgroundColor: "lightgray",
    borderRadius: 4,
    overflow: "hidden",
  },
  progressBarFilled: {
    height: "100%",
    backgroundColor: "#2E5E6E",
    borderRadius: 4,
  },
  bookCallButton: {
    alignItems: "center",
    justifyContent: "space-around",
    height: 48,
    paddingHorizontal: 12,
    borderColor: "rgba(0, 0, 0, 0.05)",
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    borderTopWidth: 1,
  },
  bookCallText: {
    color: Colors.bubbleColor,
    alignSelf: "center",
    textAlign: "center",
    marginLeft: 8,
  },
});

export default TutorialPanel;
